import { useMemo } from 'react'
import type { UserProgressDto } from '../services/stories.service'

export interface MostRecentStory {
    storyId: string
    storyTitle: string
    corpusId: string
    lastActivityAt: string
}

/**
 * Hook to find the most recently active in-progress story
 * Uses the same data source as the Dashboard's "Resume in progress" section
 *
 * MRU tie-breakers (applied in order):
 * 1. last_activity_at (or updated_at of user's story progress)
 * 2. last_viewed_at (if available) - not currently in data model
 * 3. started_at (most recent)
 * 4. story_id lexical as final stable tie-break
 */
export function useMostRecentInProgressStory(
    userProgress: UserProgressDto[] | undefined,
    isLoading: boolean = false
): MostRecentStory | null {
    return useMemo(() => {
        if (isLoading || !userProgress) {
            return null
        }

        // Ensure userProgress is an array
        if (!Array.isArray(userProgress)) {
            console.warn(
                'useMostRecentInProgressStory: userProgress is not an array:',
                userProgress
            )
            return null
        }

        const inProgressStories = userProgress.filter((p) => p.status === 'in_progress')

        if (inProgressStories.length === 0) {
            return null
        }

        if (inProgressStories.length === 1) {
            const story = inProgressStories[0]
            return {
                storyId: story.story_id,
                storyTitle: story.story_title,
                corpusId: story.corpus_id,
                lastActivityAt: story.updated_at || story.started_at || new Date().toISOString(),
            }
        }

        // Sort by MRU criteria
        const sorted = [...inProgressStories].sort((a, b) => {
            // 1. Compare by last_activity_at (updated_at) - most recent first
            const aUpdated = a.updated_at ? new Date(a.updated_at).getTime() : 0
            const bUpdated = b.updated_at ? new Date(b.updated_at).getTime() : 0

            if (aUpdated !== bUpdated) {
                return bUpdated - aUpdated // Most recent first
            }

            // 2. Compare by started_at - most recent first
            const aStarted = a.started_at ? new Date(a.started_at).getTime() : 0
            const bStarted = b.started_at ? new Date(b.started_at).getTime() : 0

            if (aStarted !== bStarted) {
                return bStarted - aStarted // Most recent first
            }

            // 3. Lexical comparison of story_id as final tie-breaker
            return a.story_id.localeCompare(b.story_id)
        })

        const mostRecent = sorted[0]
        return {
            storyId: mostRecent.story_id,
            storyTitle: mostRecent.story_title,
            corpusId: mostRecent.corpus_id,
            lastActivityAt:
                mostRecent.updated_at || mostRecent.started_at || new Date().toISOString(),
        }
    }, [userProgress, isLoading])
}
